import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { ChevronRight, ChevronLeft, Upload, X, GripVertical, Trash2, Wifi, Wind, Flame, ChefHat, Package, Zap, UtensilsCrossed, Droplets, Coffee, RefreshCw, RotateCw, Sparkles, Fan, Tv, Monitor, Car, Waves, Droplet, Dumbbell, Thermometer, Sun, Leaf, Utensils, TreePine, Eye, Umbrella, Baby, Laptop, ArrowUpDown, Accessibility, KeyRound, BedDouble, Layers, PawPrint, Mail } from 'lucide-react';
import { AddressLookupInput } from '../components/AddressLookupInput';
import { OSMMap } from '../components/OSMMap';
import { API, getAdminAuthHeaders, wakeUpBackend } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';

const FORM_I18N = {
  es: {
    loadingPropertyData: 'Cargando datos de la propiedad...',
    titleCreate: 'Crea tu Pagina Web',
    titleEdit: 'Editar Propiedad',
    titleClone: 'Clonar Propiedad',
    titleClientEdit: 'Editar mi Web',
    subtitleCreate: 'Completa el formulario para crear tu sitio web profesional',
    subtitleEdit: 'Modifica la informacion de la propiedad y guarda los cambios.',
    subtitleClone: 'Hemos precargado los datos de otra propiedad. Solo cambia la licencia y ajusta lo que necesites.',
    subtitleClientEdit: 'Hemos precargado tu propiedad actual. Revisa los cambios y confirma el pago para que podamos revisarlos y publicarlos.',
    contactLabel: 'Contacto:',
    cloneWarningPrefix: 'Clonando desde',
    cloneWarningText: 'Las imagenes existentes se reutilizan como referencia; si subes nuevas, se guardaran en la carpeta Cloudinary de la nueva licencia.',
    backToDashboard: 'Volver al panel',
    viewCurrentPreview: 'Ver preview actual',
    stepOf: 'Paso',
    of: 'de',
    previous: 'Anterior',
    next: 'Siguiente',
    previewChanges: 'Previsualizar cambios',
    saving: 'Guardando...',
    saveChanges: 'Guardar Cambios',
    createCopy: 'Crear Copia',
    viewPreview: 'Ver Vista Previa',
    steps: ['Plan y Basicos', 'Capacidad', 'Descripcion', 'Reglas', 'Servicios', 'Precios', 'Contacto', 'Fotos'],
    loadingImage: 'No se pudo leer la imagen',
    uploadProgress: 'Progreso de subida',
    compressing: 'Comprimiendo...',
    uploadError: 'Error',
    draggingHint: 'Arrastra las imagenes para cambiar el orden',
    removeAllPhotos: 'Borrar todas las fotos',
    cover: 'Portada',
    image: 'Imagen',
    uploadPhotosTitle: 'Galeria de Fotos',
    uploadPhotosSubtitle: 'Sube hasta {maxImages} imagenes de tu alojamiento',
    uploadingPhotos: 'Subiendo imagenes...',
    clickToUpload: 'Click para subir imagenes o arrastra aqui',
    uploadedCount: 'imagenes subidas',
    uploadErrorGeneric: 'Error al subir las imagenes. Intentalo de nuevo.',
    uploadNeedLicense: 'Introduce primero un numero de licencia valido antes de subir fotos',
    uploadMaxPerPlan: 'Maximo {maxImages} imagenes permitidas para el plan {plan}',
    uploadTooLarge: 'Hay imagenes que superan el limite de {maxSize} por archivo',
    selectedPlanFallback: 'seleccionado',
    validateLicenseRequired: 'El numero de licencia es obligatorio para continuar',
    validateRequiredFields: 'Por favor completa todos los campos obligatorios',
    validateCapacity: 'Por favor completa la informacion de capacidad',
    validateDescription: 'Por favor anade una descripcion del alojamiento',
    validateContact: 'Por favor anade informacion de contacto',
    validateImage: 'Por favor sube al menos una imagen',
  },
  en: {
    loadingPropertyData: 'Loading property data...',
    titleCreate: 'Create your Website',
    titleEdit: 'Edit Property',
    titleClone: 'Clone Property',
    titleClientEdit: 'Edit my Website',
    subtitleCreate: 'Complete the form to create your professional website',
    subtitleEdit: 'Update the property information and save your changes.',
    subtitleClone: 'We preloaded data from another property. Just change the license and adjust what you need.',
    subtitleClientEdit: 'We preloaded your current property. Review the changes and confirm payment so we can review and publish them.',
    contactLabel: 'Contact:',
    cloneWarningPrefix: 'Cloning from',
    cloneWarningText: 'Existing images are reused as reference; if you upload new ones, they will be stored in the new license Cloudinary folder.',
    backToDashboard: 'Back to dashboard',
    viewCurrentPreview: 'View current preview',
    stepOf: 'Step',
    of: 'of',
    previous: 'Previous',
    next: 'Next',
    previewChanges: 'Preview changes',
    saving: 'Saving...',
    saveChanges: 'Save Changes',
    createCopy: 'Create Copy',
    viewPreview: 'View Preview',
    steps: ['Plan & Basics', 'Capacity', 'Description', 'Rules', 'Services', 'Pricing', 'Contact', 'Photos'],
    loadingImage: 'Could not read image',
    uploadProgress: 'Upload progress',
    compressing: 'Compressing...',
    uploadError: 'Error',
    draggingHint: 'Drag images to reorder',
    removeAllPhotos: 'Delete all photos',
    cover: 'Cover',
    image: 'Image',
    uploadPhotosTitle: 'Photo Gallery',
    uploadPhotosSubtitle: 'Upload up to {maxImages} property images',
    uploadingPhotos: 'Uploading images...',
    clickToUpload: 'Click to upload images or drag files here',
    uploadedCount: 'images uploaded',
    uploadErrorGeneric: 'Could not upload images. Please try again.',
    uploadNeedLicense: 'Enter a valid license number before uploading photos',
    uploadMaxPerPlan: 'Maximum {maxImages} images allowed for the {plan} plan',
    uploadTooLarge: 'Some images exceed the limit of {maxSize} per file',
    selectedPlanFallback: 'selected',
    validateLicenseRequired: 'License number is required to continue',
    validateRequiredFields: 'Please complete all required fields',
    validateCapacity: 'Please complete capacity information',
    validateDescription: 'Please add a property description',
    validateContact: 'Please add contact information',
    validateImage: 'Please upload at least one image',
  },
};

const SERVICE_LABELS_EN = {
  'Aire acondicionado': 'Air conditioning',
  'Calefacción': 'Heating',
  'Cocina equipada': 'Equipped kitchen',
  'Nevera': 'Fridge',
  'Microondas': 'Microwave',
  'Horno': 'Oven',
  'Lavavajillas': 'Dishwasher',
  'Cafetera': 'Coffee maker',
  'Lavadora': 'Washer',
  'Secadora': 'Dryer',
  'Plancha': 'Iron',
  'Secador de pelo': 'Hair dryer',
  'Parking gratuito': 'Free parking',
  'Piscina': 'Pool',
  'Piscina climatizada': 'Heated pool',
  'Jacuzzi': 'Hot tub',
  'Gimnasio': 'Gym',
  'Sauna': 'Sauna',
  'Terraza/Balcón': 'Terrace/Balcony',
  'Jardín': 'Garden',
  'Barbacoa': 'BBQ',
  'Patio exterior': 'Outdoor patio',
  'Vistas al mar': 'Sea view',
  'Primera línea de playa': 'Beachfront',
  'Cuna': 'Crib',
  'Trona para bebé': 'High chair',
  'Zona de trabajo': 'Workspace',
  'Chimenea': 'Fireplace',
  'Ascensor': 'Elevator',
  'Acceso sin escaleras': 'Step-free access',
  'Check-in autónomo': 'Self check-in',
  'Ropa de cama': 'Bed linen',
  'Toallas': 'Towels',
  'Admite mascotas': 'Pet friendly',
};

const translateTemplate = (template, params = {}) => {
  return String(template || '').replace(/\{(\w+)\}/g, (_, key) => {
    return params[key] !== undefined ? String(params[key]) : `{${key}}`;
  });
};

const MAX_IMAGE_INPUT_BYTES = 25 * 1024 * 1024;
const MAX_IMAGE_UPLOAD_BYTES = 1024 * 1024;
const TARGET_IMAGE_BYTES = 180 * 1024;
const MAX_IMAGE_DIMENSION = 1920;
const WEBP_QUALITIES = [0.82, 0.74, 0.66, 0.58, 0.5, 0.42, 0.35];
const SCALE_STEPS = [1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4];

const SERVICE_ICONS = {
  'WiFi': Wifi,
  'Aire acondicionado': Wind,
  'Calefacción': Flame,
  'Cocina equipada': ChefHat,
  'Nevera': Package,
  'Microondas': Zap,
  'Horno': UtensilsCrossed,
  'Lavavajillas': Droplets,
  'Cafetera': Coffee,
  'Lavadora': RefreshCw,
  'Secadora': RotateCw,
  'Plancha': Sparkles,
  'Secador de pelo': Fan,
  'TV': Tv,
  'Smart TV': Monitor,
  'Parking gratuito': Car,
  'Piscina': Waves,
  'Piscina climatizada': Waves,
  'Jacuzzi': Droplet,
  'Gimnasio': Dumbbell,
  'Sauna': Thermometer,
  'Terraza/Balcón': Sun,
  'Jardín': Leaf,
  'Barbacoa': Utensils,
  'Patio exterior': TreePine,
  'Vistas al mar': Eye,
  'Primera línea de playa': Umbrella,
  'Cuna': Baby,
  'Trona para bebé': Baby,
  'Zona de trabajo': Laptop,
  'Chimenea': Flame,
  'Ascensor': ArrowUpDown,
  'Acceso sin escaleras': Accessibility,
  'Check-in autónomo': KeyRound,
  'Ropa de cama': BedDouble,
  'Toallas': Layers,
  'Admite mascotas': PawPrint,
};

const SERVICES_LIST = [
  'WiFi',
  'Aire acondicionado',
  'Calefacción',
  'Cocina equipada',
  'Nevera',
  'Microondas',
  'Horno',
  'Lavavajillas',
  'Cafetera',
  'Lavadora',
  'Secadora',
  'Plancha',
  'Secador de pelo',
  'TV',
  'Smart TV',
  'Parking gratuito',
  'Piscina',
  'Piscina climatizada',
  'Jacuzzi',
  'Gimnasio',
  'Sauna',
  'Terraza/Balcón',
  'Jardín',
  'Barbacoa',
  'Patio exterior',
  'Vistas al mar',
  'Primera línea de playa',
  'Cuna',
  'Trona para bebé',
  'Zona de trabajo',
  'Chimenea',
  'Ascensor',
  'Acceso sin escaleras',
  'Check-in autónomo',
  'Ropa de cama',
  'Toallas',
  'Admite mascotas'
];

const getLocalizedText = (value, language = 'es') => {
  if (typeof value === 'string') return language === 'es' ? value : '';
  if (value && typeof value === 'object') {
    return value[language] || value.es || value.en || Object.values(value).find((item) => typeof item === 'string') || '';
  }
  return '';
};

const getLocalizedList = (value, language = 'es') => {
  if (Array.isArray(value)) return language === 'es' ? value : [];
  if (value && typeof value === 'object') {
    return value[language] || value.es || value.en || Object.values(value).find(Array.isArray) || [];
  }
  return [];
};

const ensureMinimumItems = (items, minimum) => {
  const normalized = Array.isArray(items) ? [...items] : [];
  while (normalized.length < minimum) {
    normalized.push('');
  }
  return normalized;
};

const splitPolicyLines = (value) => {
  if (typeof value !== 'string') return [''];
  const lines = value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
  return lines.length > 0 ? lines : [''];
};

const joinPolicyLines = (items, fallback = '') => {
  const lines = (Array.isArray(items) ? items : [])
    .map((line) => line.trim())
    .filter((line) => line !== '');
  return lines.length > 0 ? lines.join('\n') : fallback;
};

const buildLocalizedTextValue = (spanishValue, englishValue, useLocalized) => {
  const esValue = typeof spanishValue === 'string' ? spanishValue.trim() : '';
  const enValue = typeof englishValue === 'string' ? englishValue.trim() : '';
  return useLocalized ? { es: esValue, en: enValue } : esValue;
};

const buildLocalizedListValue = (spanishItems, englishItems, useLocalized) => {
  const esItems = (Array.isArray(spanishItems) ? spanishItems : []).filter((item) => item.trim() !== '');
  const enItems = (Array.isArray(englishItems) ? englishItems : []).filter((item) => item.trim() !== '');
  return useLocalized ? { es: esItems, en: enItems } : esItems;
};

const parseCheckInRange = (value) => {
  if (!value || typeof value !== 'string') {
    return { start: '16:00', end: '21:00' };
  }

  const parts = value.split('-').map((item) => item.trim());
  return {
    start: parts[0] || '16:00',
    end: parts[1] || '21:00'
  };
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 KB';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const getApiErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (first && typeof first === 'object') {
      const loc = Array.isArray(first.loc)
        ? first.loc.filter((part) => part !== 'body').join('.')
        : '';
      const msg = first.msg || 'Validation error';
      return loc ? `${loc}: ${msg}` : msg;
    }
  }

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message;
  }

  return null;
};

const canvasToBlob = (canvas, mimeType, quality) => new Promise((resolve) => {
  canvas.toBlob((blob) => resolve(blob), mimeType, quality);
});

const loadImageFromFile = (file) => new Promise((resolve, reject) => {
  const image = new Image();
  const objectUrl = URL.createObjectURL(file);
  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('No se pudo leer la imagen'));
  };
  image.src = objectUrl;
});

const buildCompressedFilename = (name, mimeType) => {
  if (mimeType === 'image/jpeg') {
    return name.replace(/\.[^.]+$/, '') + '.jpg';
  }
  if (mimeType === 'image/webp') {
    return name.replace(/\.[^.]+$/, '') + '.webp';
  }
  return name;
};

const compressImageSmart = async (file) => {
  if (!file.type.startsWith('image/')) return file;
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;

  const image = await loadImageFromFile(file);
  const maxSide = Math.max(image.width, image.height);
  const scale = maxSide > MAX_IMAGE_DIMENSION ? (MAX_IMAGE_DIMENSION / maxSide) : 1;

  const width = Math.round(image.width * scale);
  const height = Math.round(image.height * scale);

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return file;

  // Prefer WebP for stronger compression while preserving acceptable visual quality.
  const outputMime = 'image/webp';

  let bestBlob = null;
  for (const scaleFactor of SCALE_STEPS) {
    const scaledWidth = Math.max(1, Math.round(width * scaleFactor));
    const scaledHeight = Math.max(1, Math.round(height * scaleFactor));

    canvas.width = scaledWidth;
    canvas.height = scaledHeight;
    context.clearRect(0, 0, scaledWidth, scaledHeight);
    context.drawImage(image, 0, 0, scaledWidth, scaledHeight);

    for (const quality of WEBP_QUALITIES) {
      const blob = await canvasToBlob(canvas, outputMime, quality);
      if (!blob) continue;

      if (!bestBlob || blob.size < bestBlob.size) {
        bestBlob = blob;
      }

      if (blob.size <= TARGET_IMAGE_BYTES) {
        return new File(
          [blob],
          buildCompressedFilename(file.name, outputMime),
          { type: outputMime, lastModified: Date.now() }
        );
      }
    }
  }

  if (!bestBlob) return file;
  if (bestBlob.size >= file.size * 0.98 && bestBlob.size > MAX_IMAGE_UPLOAD_BYTES) {
    return file;
  }

  return new File(
    [bestBlob],
    buildCompressedFilename(file.name, outputMime),
    { type: outputMime, lastModified: Date.now() }
  );
};

const mapPropertyToFormData = (property, { clearLicense = false, stripImagePublicIds = false } = {}) => {
  const checkIn = parseCheckInRange(property?.property_data?.check_in);
  const images = Array.isArray(property?.images)
    ? property.images.map((image, index) => ({
        ...image,
        public_id: stripImagePublicIds ? null : image.public_id,
        order: image?.order || index + 1,
      }))
    : [];

  return {
    plan: property?.plan || '',
    license_number: clearLicense ? '' : (property?.license_number || ''),
    property_name: getLocalizedText(property?.property_data?.name, 'es'),
    property_name_en: getLocalizedText(property?.property_data?.name, 'en'),
    google_maps_address: property?.location?.google_maps_address || getLocalizedText(property?.property_data?.location, 'es'),
    location_display_en: getLocalizedText(property?.property_data?.location, 'en'),
    latitude: property?.location?.latitude ?? null,
    longitude: property?.location?.longitude ?? null,
    guests: property?.property_data?.stats?.guests?.toString() || '',
    bedrooms: property?.property_data?.stats?.bedrooms?.toString() || '',
    beds: property?.property_data?.stats?.beds?.toString() || '',
    bathrooms: property?.property_data?.stats?.bathrooms?.toString() || '',
    description: getLocalizedText(property?.property_data?.description, 'es'),
    description_en: getLocalizedText(property?.property_data?.description, 'en'),
    area_description: getLocalizedText(property?.location?.area_description, 'es'),
    area_description_en: getLocalizedText(property?.location?.area_description, 'en'),
    nearby: ensureMinimumItems(getLocalizedList(property?.location?.nearby, 'es'), 3),
    nearby_en: ensureMinimumItems(getLocalizedList(property?.location?.nearby, 'en'), 3),
    check_in_start: checkIn.start,
    check_in_end: checkIn.end,
    check_out: property?.property_data?.check_out || '11:00 AM',
    rules: ensureMinimumItems(getLocalizedList(property?.property_data?.rules, 'es'), 4),
    rules_en: ensureMinimumItems(getLocalizedList(property?.property_data?.rules, 'en'), 4),
    cancellation_policy: splitPolicyLines(getLocalizedText(property?.property_data?.cancellation_policy, 'es')),
    cancellation_policy_en: splitPolicyLines(getLocalizedText(property?.property_data?.cancellation_policy, 'en')),
    services: Array.isArray(property?.services) ? property.services : [],
    min_price: property?.pricing?.min_price?.toString() || '',
    max_price: property?.pricing?.max_price?.toString() || '',
    pricing_mode: property?.pricing?.pricing_mode || 'simple',
    base_price: property?.pricing?.base_price?.toString() || '',
    seasonal_rates: property?.pricing?.seasonal_rates || [],
    weekend_surcharge: property?.pricing?.weekend_surcharge || { enabled: false, type: 'percentage', value: '' },
    duration_discounts: property?.pricing?.duration_discounts || { week: '', month: '' },
    ics_urls: Array.isArray(property?.booking?.ics_urls) && property.booking.ics_urls.length > 0 ? property.booking.ics_urls : [''],
    booking_links: Array.isArray(property?.booking?.booking_links) && property.booking.booking_links.length > 0 ? property.booking.booking_links : [''],
    whatsapp: property?.contact?.whatsapp || '',
    email: property?.contact?.email || '',
    preferred_netlify_subdomain: property?.booking?.preferred_netlify_subdomain || '',
    custom_domain: property?.booking?.custom_domain || property?.publication?.custom_domain || '',
    images
  };
};

export const SubmissionFormPage = () => {
  const { language } = useLanguage();
  const t = FORM_I18N[language] || FORM_I18N.es;
  const tr = (esText, enText) => (language === 'en' ? enText : esText);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { license_number: editingLicense, manageToken } = useParams();
  const cloningLicense = (searchParams.get('cloneFrom') || '').trim().toUpperCase();
  const isEditMode = Boolean(editingLicense);
  const isCloneMode = !isEditMode && Boolean(cloningLicense);
  const isClientEditMode = Boolean(manageToken);
  const DRAFT_KEY = isCloneMode
    ? `submission_form_draft_clone_${cloningLicense}`
    : 'submission_form_draft';
  const [currentStep, setCurrentStep] = useState(() => {
    if (isEditMode || isClientEditMode) return 1;
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      return saved ? (JSON.parse(saved).__step || 1) : 1;
    } catch { return 1; }
  });
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(isEditMode || isCloneMode || isClientEditMode);
  const [loadedProperty, setLoadedProperty] = useState(null);

  const getInitialFormData = () => {
    const defaults = {
    // Step 1: Plan y datos básicos
    plan: '',
    license_number: '',
    property_name: '',
    property_name_en: '',
    google_maps_address: '',
    location_display_en: '',
    latitude: null,
    longitude: null,
    
    // Step 2: Capacidad
    guests: '',
    bedrooms: '',
    beds: '',
    bathrooms: '',
    
    // Step 3: Descripción
    description: '',
    description_en: '',
    area_description: '',
    area_description_en: '',
    nearby: ['', '', ''],
    nearby_en: ['', '', ''],
    
    // Step 4: Reglas y políticas
    check_in_start: '16:00',
    check_in_end: '21:00',
    check_out: '11:00 AM',
    rules: ['', '', '', ''],
    rules_en: ['', '', '', ''],
    cancellation_policy: [''],
    cancellation_policy_en: [''],
    
    // Step 5: Servicios
    services: [],
    
    // Step 6: Precios y enlaces
    min_price: '',
    max_price: '',
    pricing_mode: 'simple',
    base_price: '',
    seasonal_rates: [],
    weekend_surcharge: { enabled: false, type: 'percentage', value: '' },
    duration_discounts: { week: '', month: '' },
    ics_urls: [''],
    booking_links: [''],

    // Step 7: Contacto
    whatsapp: '',
    email: '',
    preferred_netlify_subdomain: '',
    custom_domain: '',
    
    // Step 8: Imágenes
    images: []
    };

    if (!isEditMode && !isClientEditMode) {
      try {
        const saved = localStorage.getItem(DRAFT_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          delete parsed.__step;
          if (typeof parsed.cancellation_policy === 'string') {
            parsed.cancellation_policy = splitPolicyLines(parsed.cancellation_policy);
          }
          if (!Array.isArray(parsed.cancellation_policy)) {
            parsed.cancellation_policy = [''];
          }
          if (typeof parsed.cancellation_policy_en === 'string') {
            parsed.cancellation_policy_en = splitPolicyLines(parsed.cancellation_policy_en);
          }
          if (!Array.isArray(parsed.cancellation_policy_en)) {
            parsed.cancellation_policy_en = [''];
          }
          // Merge with defaults so new fields added after a draft was saved still get their default value
          return { ...defaults, ...parsed };
        }
      } catch { /* ignore */ }
    }
    return defaults;
  };

  const [formData, setFormData] = useState(getInitialFormData);

  // Persist draft to localStorage on every change (new submissions and clone mode)
  useEffect(() => {
    if (isEditMode || isClientEditMode) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...formData, __step: currentStep }));
    } catch { /* quota exceeded – ignore */ }
  }, [formData, currentStep, isEditMode, isClientEditMode, DRAFT_KEY]);

  useEffect(() => {
    wakeUpBackend();
  }, []);

  useEffect(() => {
    const loadPropertyForForm = async () => {
      if (isClientEditMode) {
        try {
          setInitializing(true);
          const response = await axios.get(`${API}/modifications/manage/${manageToken}/property`);
          setLoadedProperty(response.data);
          setFormData(mapPropertyToFormData(response.data));
        } catch (error) {
          console.error('Error loading property for client edit:', error);
          toast.error(
            language === 'en'
              ? 'Could not load the property. The link may be expired or invalid.'
              : 'No se pudo cargar la propiedad. El enlace puede estar caducado o ser incorrecto.'
          );
          navigate(`/manage/${manageToken}`);
        } finally {
          setInitializing(false);
        }
        return;
      }

      const sourceLicense = editingLicense || cloningLicense;
      if (!sourceLicense) {
        setInitializing(false);
        return;
      }

      try {
        setInitializing(true);
        const response = await axios.get(`${API}/properties/${sourceLicense}`, {
          headers: {
            ...getAdminAuthHeaders()
          }
        });
        setLoadedProperty(response.data);
        setFormData(mapPropertyToFormData(response.data, {
          clearLicense: isCloneMode,
          stripImagePublicIds: isCloneMode,
        }));
      } catch (error) {
        console.error(isCloneMode ? 'Error loading property for clone:' : 'Error loading property for edit:', error);
        if (error?.response?.status === 401) {
          toast.error(
            language === 'en'
              ? 'This edit route is only available for authenticated admin users.'
              : 'Esta ruta de edicion es solo para administracion autenticada.'
          );
          if (sourceLicense) {
            navigate(`/preview/${sourceLicense}`);
          } else {
            navigate('/');
          }
        } else {
          toast.error(
            isCloneMode
              ? (language === 'en' ? 'Could not load the property to clone' : 'No se pudo cargar la propiedad para clonar')
              : (language === 'en' ? 'Could not load the property to edit' : 'No se pudo cargar la propiedad para editar')
          );
          navigate('/admin/dashboard');
        }
      } finally {
        setInitializing(false);
      }
    };

    loadPropertyForForm();
  }, [cloningLicense, editingLicense, isClientEditMode, isCloneMode, language, manageToken, navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayField = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayField = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState([]);
  const maxImages = formData.plan === 'premium' ? 30 : formData.plan === 'plus' ? 20 : 10;

  const upsertProgress = (id, patch) => {
    setImageUploadProgress(prev => {
      const existingIndex = prev.findIndex(item => item.id === id);
      if (existingIndex === -1) {
        return [...prev, { id, ...patch }];
      }
      const next = [...prev];
      next[existingIndex] = { ...next[existingIndex], ...patch };
      return next;
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const normalizedLicense = (formData.license_number || editingLicense || '').trim().toUpperCase();
    if (normalizedLicense.length < 3) {
      toast.error(t.uploadNeedLicense);
      e.target.value = '';
      return;
    }

    if (formData.images.length + files.length > maxImages) {
      toast.error(translateTemplate(t.uploadMaxPerPlan, {
        maxImages,
        plan: formData.plan || t.selectedPlanFallback,
      }));
      return;
    }

    const oversizedFiles = files.filter(file => file.size > MAX_IMAGE_INPUT_BYTES);
    if (oversizedFiles.length > 0) {
      toast.error(translateTemplate(t.uploadTooLarge, {
        maxSize: formatBytes(MAX_IMAGE_INPUT_BYTES),
      }));
      e.target.value = '';
      return;
    }

    setUploadingImages(true);
    try {
      // Get signed upload params from backend
      const sigRes = isClientEditMode
        ? await axios.get(`${API}/modifications/manage/${manageToken}/upload-signature`)
        : await axios.get(`${API}/upload/signature`, {
            params: { license_number: normalizedLicense },
            headers: getAdminAuthHeaders(),
          });
      const { timestamp, signature, api_key, cloud_name, folder } = sigRes.data;

      for (const [index, file] of files.entries()) {
        const uploadId = `${Date.now()}-${index}-${file.name}`;
        upsertProgress(uploadId, {
          name: file.name,
          status: 'compressing',
          progress: 0,
          originalSize: file.size,
          finalSize: null,
        });

        const optimizedFile = await compressImageSmart(file);

        if (optimizedFile.size > MAX_IMAGE_UPLOAD_BYTES) {
          upsertProgress(uploadId, {
            status: 'error',
            progress: 100,
            finalSize: optimizedFile.size,
            message: tr(
              `Supera el maximo de ${formatBytes(MAX_IMAGE_UPLOAD_BYTES)} incluso tras compresion`,
              `Still exceeds the ${formatBytes(MAX_IMAGE_UPLOAD_BYTES)} limit after compression`
            ),
          });
          continue;
        }

        upsertProgress(uploadId, {
          status: 'uploading',
          progress: 5,
          finalSize: optimizedFile.size,
        });

        const fd = new FormData();
        fd.append('file', optimizedFile);
        fd.append('timestamp', timestamp);
        fd.append('signature', signature);
        fd.append('api_key', api_key);
        fd.append('folder', folder);

        let uploadRes;
        try {
          uploadRes = await axios.post(
            `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
            fd,
            {
              onUploadProgress: (event) => {
                if (!event.total) return;
                const progress = Math.max(5, Math.round((event.loaded * 100) / event.total));
                upsertProgress(uploadId, { progress });
              },
            }
          );
        } catch (uploadErr) {
          const detail = uploadErr?.response?.data?.error?.message || tr('Error al subir', 'Upload failed');
          upsertProgress(uploadId, {
            status: 'error',
            progress: 0,
            message: detail,
          });
          continue;
        }

        if (!uploadRes?.data?.secure_url) {
          upsertProgress(uploadId, {
            status: 'error',
            progress: 0,
            message: tr('Error al subir', 'Upload failed'),
          });
          continue;
        }
        const { secure_url, public_id } = uploadRes.data;

        setFormData(prev => ({
          ...prev,
          images: [...prev.images, {
            url: secure_url,
            public_id,
            order: prev.images.length + 1,
          }],
        }));

        upsertProgress(uploadId, {
          status: 'done',
          progress: 100,
          message: tr('Completada', 'Completed'),
        });
      }
    } catch (err) {
      console.error('Image upload error:', err);
      const apiMessage = getApiErrorMessage(err);
      const uploadMessage = apiMessage || err?.response?.data?.detail || err?.response?.data?.error?.message;
      toast.error(uploadMessage || t.uploadErrorGeneric);
    } finally {
      setUploadingImages(false);
      // Reset input so the same file can be selected again if needed
      e.target.value = '';
    }
  };

  const dragIndexRef = useRef(null);

  const handleDragStart = (index) => {
    dragIndexRef.current = index;
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === null || from === index) return;
    setFormData(prev => {
      const imgs = [...prev.images];
      const [moved] = imgs.splice(from, 1);
      imgs.splice(index, 0, moved);
      dragIndexRef.current = index;
      return { ...prev, images: imgs.map((img, i) => ({ ...img, order: i + 1 })) };
    });
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
  };

  const removeImage = async (index) => {
    const image = formData.images[index];
    const normalizedLicense = (formData.license_number || editingLicense || loadedProperty?.license_number || '').trim().toUpperCase();
    // Delete from Cloudinary if it has a public_id
    if (image?.public_id && normalizedLicense.length >= 3) {
      try {
        if (isClientEditMode) {
          await axios.delete(
            `${API}/upload/image/${encodeURIComponent(image.public_id)}`,
            { params: { license_number: normalizedLicense } }
          );
        } else {
          await axios.delete(
            `${API}/upload/image/${encodeURIComponent(image.public_id)}`,
            { params: { license_number: normalizedLicense }, headers: getAdminAuthHeaders() }
          );
        }
      } catch (err) {
        console.warn('Could not delete image from Cloudinary:', err);
      }
    }
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index).map((img, i) => ({
        ...img,
        order: i + 1,
      })),
    }));
  };

  const handleClearAllImages = async () => {
    if (!window.confirm(tr('Borrar todas las fotos? Esta accion no se puede deshacer.', 'Delete all photos? This action cannot be undone.'))) return;
    const normalizedLicense = (formData.license_number || editingLicense || loadedProperty?.license_number || '').trim().toUpperCase();
    for (const image of formData.images) {
      if (image?.public_id && normalizedLicense.length >= 3) {
        try {
          if (isClientEditMode) {
            await axios.delete(
              `${API}/upload/image/${encodeURIComponent(image.public_id)}`,
              { params: { license_number: normalizedLicense } }
            );
          } else {
            await axios.delete(
              `${API}/upload/image/${encodeURIComponent(image.public_id)}`,
              { params: { license_number: normalizedLicense }, headers: getAdminAuthHeaders() }
            );
          }
        } catch (err) {
          console.warn('Could not delete image from Cloudinary:', err);
        }
      }
    }
    setFormData(prev => ({ ...prev, images: [] }));
  };

  // Solo mostrar campos de traducción EN en modo edición admin.
  // Los clientes (modo crear o edición por token) no ven traducciones.
  const showTranslationFields = isEditMode && !isClientEditMode;

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.license_number?.trim()) {
          toast.error(t.validateLicenseRequired);
          return false;
        }
        if (!formData.plan || !formData.property_name || !formData.google_maps_address) {
          toast.error(t.validateRequiredFields);
          return false;
        }
        break;
      case 2:
        if (!formData.guests || !formData.bedrooms || !formData.beds || !formData.bathrooms) {
          toast.error(t.validateCapacity);
          return false;
        }
        break;
      case 3:
        if (!formData.description) {
          toast.error(t.validateDescription);
          return false;
        }
        break;
      case 7:
        if (!formData.whatsapp || !formData.email) {
          toast.error(t.validateContact);
          return false;
        }
        break;
      case 8:
        if (formData.images.length === 0) {
          toast.error(t.validateImage);
          return false;
        }
        break;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 8));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const buildPropertyPayload = () => {
    let normalizedMinPrice, normalizedMaxPrice;

    if (formData.pricing_mode === 'advanced') {
      // Calculate min/max from base_price + seasonal rates
      const base = parseFloat(formData.base_price) || 0;
      const allRates = [base, ...(formData.seasonal_rates || []).map(r => {
        if (r.rate_type === 'fixed') return parseFloat(r.rate_value) || base;
        return base * (1 + (parseFloat(r.rate_value) || 0) / 100);
      })];
      normalizedMinPrice = Math.min(...allRates);
      normalizedMaxPrice = Math.max(...allRates);
      // Apply weekend surcharge to max if enabled
      if (formData.weekend_surcharge?.enabled) {
        const sv = parseFloat(formData.weekend_surcharge.value) || 0;
        const maxWithSurcharge = formData.weekend_surcharge.type === 'fixed'
          ? normalizedMaxPrice + sv
          : normalizedMaxPrice * (1 + sv / 100);
        normalizedMaxPrice = Math.max(normalizedMaxPrice, maxWithSurcharge);
      }
      normalizedMinPrice = Math.round(normalizedMinPrice * 100) / 100;
      normalizedMaxPrice = Math.round(normalizedMaxPrice * 100) / 100;
    } else {
      const rawMinPrice = formData.min_price?.toString().trim();
      const rawMaxPrice = formData.max_price?.toString().trim();
      const parsedMinPrice = rawMinPrice === '' ? null : parseFloat(rawMinPrice);
      const parsedMaxPrice = rawMaxPrice === '' ? null : parseFloat(rawMaxPrice);
      const minPrice = Number.isNaN(parsedMinPrice) ? null : parsedMinPrice;
      const maxPrice = Number.isNaN(parsedMaxPrice) ? null : parsedMaxPrice;
      normalizedMinPrice = minPrice ?? maxPrice ?? 0;
      normalizedMaxPrice = maxPrice ?? minPrice ?? 0;
    }

    const useLocalizedPayload = showTranslationFields || isClientEditMode;

    const normalizedImages = (Array.isArray(formData.images) ? [...formData.images] : [])
      .sort((left, right) => {
        const leftOrder = Number.parseInt(left?.order, 10);
        const rightOrder = Number.parseInt(right?.order, 10);
        const leftValid = Number.isFinite(leftOrder) && leftOrder > 0;
        const rightValid = Number.isFinite(rightOrder) && rightOrder > 0;
        if (!leftValid && !rightValid) return 0;
        if (!leftValid) return 1;
        if (!rightValid) return -1;
        return leftOrder - rightOrder;
      })
      .map((image, index) => ({
        ...image,
        order: index + 1,
      }));

    const buildTextWithFallback = (spanishValue, englishValue, existingValue) => {
      const esValue = typeof spanishValue === 'string' ? spanishValue.trim() : '';
      const enFromForm = typeof englishValue === 'string' ? englishValue.trim() : '';
      const enFromExisting = getLocalizedText(existingValue, 'en').trim();
      const resolvedEn = enFromForm || enFromExisting;
      return useLocalizedPayload ? { es: esValue, en: resolvedEn } : esValue;
    };

    const buildListWithFallback = (spanishItems, englishItems, existingValue) => {
      const esItems = (Array.isArray(spanishItems) ? spanishItems : [])
        .map((item) => item.trim())
        .filter((item) => item !== '');
      const enItemsFromForm = (Array.isArray(englishItems) ? englishItems : [])
        .map((item) => item.trim())
        .filter((item) => item !== '');
      const enItemsFromExisting = getLocalizedList(existingValue, 'en')
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter((item) => item !== '');
      const resolvedEnItems = enItemsFromForm.length > 0 ? enItemsFromForm : enItemsFromExisting;
      return useLocalizedPayload ? { es: esItems, en: resolvedEnItems } : esItems;
    };

    return {
      plan: formData.plan,
      property_data: {
        name: buildTextWithFallback(formData.property_name, formData.property_name_en, loadedProperty?.property_data?.name),
        location: buildTextWithFallback(formData.google_maps_address, formData.location_display_en, loadedProperty?.property_data?.location),
        description: buildTextWithFallback(formData.description, formData.description_en, loadedProperty?.property_data?.description),
        stats: {
          guests: parseInt(formData.guests) || 0,
          bedrooms: parseInt(formData.bedrooms) || 0,
          beds: parseInt(formData.beds) || 0,
          bathrooms: parseInt(formData.bathrooms) || 0
        },
        features: loadedProperty?.property_data?.features || [],
        rules: buildListWithFallback(formData.rules, formData.rules_en, loadedProperty?.property_data?.rules),
        cancellation_policy: buildTextWithFallback(
          joinPolicyLines(formData.cancellation_policy, 'Flexible'),
          joinPolicyLines(formData.cancellation_policy_en),
          loadedProperty?.property_data?.cancellation_policy
        ),
        check_in: `${formData.check_in_start} - ${formData.check_in_end}`,
        check_out: formData.check_out
      },
      images: normalizedImages,
      location: {
        google_maps_address: formData.google_maps_address,
        latitude: formData.latitude,
        longitude: formData.longitude,
        area_description: buildTextWithFallback(formData.area_description, formData.area_description_en, loadedProperty?.location?.area_description),
        nearby: buildListWithFallback(formData.nearby, formData.nearby_en, loadedProperty?.location?.nearby)
      },
      booking: {
        ics_urls: formData.ics_urls.filter(u => u.trim()),
        booking_links: formData.booking_links.filter(u => u.trim()),
        preferred_netlify_subdomain: formData.preferred_netlify_subdomain || null,
        custom_domain: formData.custom_domain || null
      },
      pricing: {
        min_price: normalizedMinPrice,
        max_price: normalizedMaxPrice,
        pricing_mode: formData.pricing_mode || 'simple',
        ...(formData.pricing_mode === 'advanced' ? {
          base_price: parseFloat(formData.base_price) || 0,
          seasonal_rates: (formData.seasonal_rates || []).filter(r => r.label && r.date_start && r.date_end),
          weekend_surcharge: formData.weekend_surcharge?.enabled ? {
            enabled: true,
            type: formData.weekend_surcharge.type || 'percentage',
            value: parseFloat(formData.weekend_surcharge.value) || 0,
          } : { enabled: false, type: 'percentage', value: 0 },
          duration_discounts: {
            week: formData.duration_discounts?.week !== '' ? parseFloat(formData.duration_discounts?.week) || null : null,
            month: formData.duration_discounts?.month !== '' ? parseFloat(formData.duration_discounts?.month) || null : null,
          },
        } : {}),
      },
      contact: {
        whatsapp: formData.whatsapp,
        email: formData.email
      },
      services: formData.services
    };
  };

  const handleSubmit = async () => {
    if (!validateStep(8)) return;

    setLoading(true);
    try {
      const propertyData = buildPropertyPayload();

      if (isClientEditMode) {
        const response = await axios.post(`${API}/modifications/manage/${manageToken}/checkout/session`, {
          token: manageToken,
          origin_url: window.location.origin,
          property_payload: propertyData,
        });

        if (!response.data?.url) {
          throw new Error('No checkout URL returned');
        }

        window.location.href = response.data.url;
        return;
      }

      if (isEditMode) {
        await axios.put(`${API}/properties/${editingLicense}`, propertyData, {
          headers: {
            ...getAdminAuthHeaders()
          }
        });
        toast.success(tr('Cambios guardados con exito', 'Changes saved successfully'));
      } else {
        const createResponse = await axios.post(`${API}/properties/`, {
          ...propertyData,
          license_number: formData.license_number
        });

        const createdLicense = createResponse?.data?.license_number || formData.license_number;
        const createdManageToken = createResponse?.data?.manage_token || null;
        if (createdLicense && createdManageToken) {
          sessionStorage.setItem(`preview_manage_token:${createdLicense}`, createdManageToken);
        }

        localStorage.removeItem(DRAFT_KEY);
        toast.success(tr('Propiedad creada con exito', 'Property created successfully'));

        const previewUrl = createdManageToken
          ? `/preview/${createdLicense}?manage_token=${encodeURIComponent(createdManageToken)}`
          : `/preview/${createdLicense}`;
        navigate(previewUrl);
        return;
      }

      navigate(`/preview/${editingLicense || formData.license_number}`);
    } catch (error) {
      const isUpdateFlow = isEditMode || isClientEditMode;
      console.error(isUpdateFlow ? 'Error updating property:' : 'Error creating property:', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || (isUpdateFlow
        ? tr('Error al enviar la actualizacion', 'Failed to submit the update')
        : tr('Error al crear la propiedad', 'Failed to create the property')));
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="plan">{tr('Plan', 'Plan')}</Label>
              {isClientEditMode ? (
                <>
                  <Input
                    id="plan"
                    value={formData.plan ? `${formData.plan.charAt(0).toUpperCase()}${formData.plan.slice(1)}` : ''}
                    disabled
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {tr('El plan no se puede modificar desde este enlace privado.', 'The plan cannot be changed from this private link.')}
                  </p>
                </>
              ) : (
                <Select value={formData.plan} onValueChange={(value) => handleChange('plan', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder={tr('Selecciona un plan', 'Select a plan')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">{tr('Basico', 'Basic')} - 49€</SelectItem>
                    <SelectItem value="plus">Plus - 69€</SelectItem>
                    <SelectItem value="premium">Premium - 89€</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div>
              <Label htmlFor="license_number">{tr('Numero de Licencia *', 'License Number *')}</Label>
              <Input
                id="license_number"
                value={formData.license_number}
                onChange={(e) => handleChange('license_number', e.target.value)}
                placeholder={tr('Ej: VUT-123-456', 'Eg: VUT-123-456')}
                disabled={isEditMode || isClientEditMode}
              />
              <p className="text-sm text-gray-500 mt-1">
                {(isEditMode || isClientEditMode)
                  ? tr('La licencia se mantiene fija y no se puede modificar.', 'The license stays fixed and cannot be changed.')
                  : isCloneMode
                    ? tr('Introduce una licencia nueva y unica antes de guardar o subir nuevas imagenes.', 'Enter a new unique license before saving or uploading new images.')
                    : tr('Este numero se usara como identificador unico', 'This number will be used as a unique identifier')}
              </p>
            </div>

            <div>
              <Label htmlFor="property_name">{tr('Nombre del Alojamiento *', 'Property Name *')}</Label>
              <Input
                id="property_name"
                value={formData.property_name}
                onChange={(e) => handleChange('property_name', e.target.value)}
                placeholder={tr('Ej: Villa Paraiso Costero', 'Eg: Seaside Paradise Villa')}
              />
            </div>

            {showTranslationFields && (
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-4 space-y-4">
                <p className="text-sm font-semibold text-blue-900">{tr('Version en ingles (editable)', 'English version (editable)')}</p>
                <div>
                  <Label htmlFor="property_name_en">{tr('Nombre del alojamiento en ingles', 'Property name in English')}</Label>
                  <Input
                    id="property_name_en"
                    value={formData.property_name_en}
                    onChange={(e) => handleChange('property_name_en', e.target.value)}
                    placeholder="Eg: Seaside Villa"
                  />
                </div>
                <div>
                  <Label htmlFor="location_display_en">{tr('Ubicacion visible en ingles', 'Visible location in English')}</Label>
                  <Input
                    id="location_display_en"
                    value={formData.location_display_en}
                    onChange={(e) => handleChange('location_display_en', e.target.value)}
                    placeholder="Eg: Salou, Tarragona, Spain"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="google_maps_address">{tr('Direccion *', 'Address *')}</Label>
              <AddressLookupInput
                value={formData.google_maps_address}
                onChange={(value) => {
                  handleChange('google_maps_address', value);
                  handleChange('latitude', null);
                  handleChange('longitude', null);
                }}
                onSelect={(selection) => {
                  handleChange('google_maps_address', selection.address);
                  handleChange('latitude', selection.latitude);
                  handleChange('longitude', selection.longitude);
                }}
                placeholder={tr('Ej: Playa del Carmen, Quintana Roo, Mexico', 'Eg: Playa del Carmen, Quintana Roo, Mexico')}
              />
              <p className="text-sm text-gray-500 mt-1">
                {tr('Busca y selecciona una direccion para guardar la ubicacion exacta.', 'Search and select an address to save the exact location.')}
              </p>
            </div>

            {formData.google_maps_address && (
              <div>
                <Label>{tr('Vista previa del mapa', 'Map preview')}</Label>
                <div className="mt-2">
                  <OSMMap
                    address={formData.google_maps_address}
                    latitude={formData.latitude}
                    longitude={formData.longitude}
                    markerLabel={formData.property_name || tr('Ubicacion del alojamiento', 'Property location')}
                    heightClassName="h-56"
                  />
                </div>
              </div>
            )}

            {formData.latitude !== null && formData.longitude !== null && (
              <p className="text-xs text-gray-500">
                {tr('Coordenadas', 'Coordinates')}: {Number(formData.latitude).toFixed(6)}, {Number(formData.longitude).toFixed(6)}
              </p>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">{tr('Capacidad del Alojamiento', 'Property Capacity')}</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="guests">{tr('Numero maximo de Huespedes *', 'Maximum Guests *')}</Label>
                <Input
                  id="guests"
                  type="number"
                  min="1"
                  value={formData.guests}
                  onChange={(e) => handleChange('guests', e.target.value)}
                  placeholder="4"
                />
              </div>

              <div>
                <Label htmlFor="bedrooms">{tr('Numero de Dormitorios *', 'Bedrooms *')}</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  min="1"
                  value={formData.bedrooms}
                  onChange={(e) => handleChange('bedrooms', e.target.value)}
                  placeholder="2"
                />
              </div>

              <div>
                <Label htmlFor="beds">{tr('Numero de Camas *', 'Beds *')}</Label>
                <Input
                  id="beds"
                  type="number"
                  min="1"
                  value={formData.beds}
                  onChange={(e) => handleChange('beds', e.target.value)}
                  placeholder="3"
                />
              </div>

              <div>
                <Label htmlFor="bathrooms">{tr('Numero de Banos *', 'Bathrooms *')}</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  min="1"
                  value={formData.bathrooms}
                  onChange={(e) => handleChange('bathrooms', e.target.value)}
                  placeholder="1"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="description">{tr('Descripcion del Alojamiento *', 'Property Description *')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={tr('Describe tu alojamiento...', 'Describe your property...')}
                rows={6}
              />
            </div>

            <div>
              <Label htmlFor="area_description">{tr('Descripcion de la Zona', 'Area Description')}</Label>
              <Textarea
                id="area_description"
                value={formData.area_description}
                onChange={(e) => handleChange('area_description', e.target.value)}
                placeholder={tr('Describe la zona y que hay cerca...', 'Describe the area and nearby places...')}
                rows={4}
              />
            </div>

            <div>
              <Label>{tr('Lugares de Interes Cercanos', 'Nearby Points of Interest')}</Label>
              {formData.nearby.map((item, index) => (
                <div key={index} className="flex gap-2 mt-2">
                  <Input
                    value={item}
                    onChange={(e) => handleArrayChange('nearby', index, e.target.value)}
                    placeholder={tr('Ej: 5 minutos a la playa', 'Eg: 5 minutes to the beach')}
                  />
                  {formData.nearby.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeArrayField('nearby', index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addArrayField('nearby')}
                className="mt-2"
              >
                {tr('Anadir otro', 'Add another')}
              </Button>
            </div>

            {showTranslationFields && (
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-4 space-y-4">
                <p className="text-sm font-semibold text-blue-900">{tr('Contenido en ingles', 'Content in English')}</p>
                <div>
                  <Label htmlFor="description_en">{tr('Descripcion del alojamiento en ingles', 'Property description in English')}</Label>
                  <Textarea
                    id="description_en"
                    value={formData.description_en}
                    onChange={(e) => handleChange('description_en', e.target.value)}
                    placeholder="Describe your property in English..."
                    rows={6}
                  />
                </div>

                <div>
                  <Label htmlFor="area_description_en">{tr('Descripcion de la zona en ingles', 'Area description in English')}</Label>
                  <Textarea
                    id="area_description_en"
                    value={formData.area_description_en}
                    onChange={(e) => handleChange('area_description_en', e.target.value)}
                    placeholder="Describe the area in English..."
                    rows={4}
                  />
                </div>

                <div>
                  <Label>{tr('Lugares cercanos en ingles', 'Nearby places in English')}</Label>
                  {formData.nearby_en.map((item, index) => (
                    <div key={index} className="flex gap-2 mt-2">
                      <Input
                        value={item}
                        onChange={(e) => handleArrayChange('nearby_en', index, e.target.value)}
                        placeholder="Eg: 5 minutes to the beach"
                      />
                      {formData.nearby_en.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeArrayField('nearby_en', index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addArrayField('nearby_en')}
                    className="mt-2"
                  >
                    {tr('Anadir otro en ingles', 'Add another in English')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">{tr('Reglas y Politicas', 'Rules and Policies')}</h3>
            
            <div>
              <Label className="mb-4 block">{tr('Intervalo de Check-in', 'Check-in window')}</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="check_in_start" className="text-sm">{tr('Desde', 'From')}</Label>
                  <Input
                    id="check_in_start"
                    type="time"
                    value={formData.check_in_start}
                    onChange={(e) => handleChange('check_in_start', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="check_in_end" className="text-sm">{tr('Hasta', 'To')}</Label>
                  <Input
                    id="check_in_end"
                    type="time"
                    value={formData.check_in_end}
                    onChange={(e) => handleChange('check_in_end', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="check_out">{tr('Hora de Check-out', 'Check-out time')}</Label>
              <Input
                id="check_out"
                value={formData.check_out}
                onChange={(e) => handleChange('check_out', e.target.value)}
                placeholder="11:00 AM"
              />
            </div>

            <div>
              <Label>{tr('Normas de la Casa', 'House Rules')}</Label>
              {formData.rules.map((rule, index) => (
                <div key={index} className="flex gap-2 mt-2">
                  <Input
                    value={rule}
                    onChange={(e) => handleArrayChange('rules', index, e.target.value)}
                    placeholder={tr('Ej: No se permiten mascotas', 'Eg: No pets allowed')}
                  />
                  {formData.rules.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeArrayField('rules', index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addArrayField('rules')}
                className="mt-2"
              >
                {tr('Anadir otra regla', 'Add another rule')}
              </Button>
            </div>

            <div>
              <Label>{tr('Politica de Cancelacion', 'Cancellation Policy')}</Label>
              <p className="text-xs text-gray-500 mb-1">{tr('Anade cada condicion por separado para mostrarla como lista.', 'Add each condition separately so it is displayed as a list.')}</p>
              {formData.cancellation_policy.map((condition, index) => (
                <div key={index} className="flex gap-2 mt-2">
                  <Input
                    value={condition}
                    onChange={(e) => handleArrayChange('cancellation_policy', index, e.target.value)}
                    placeholder={tr('Ej: Reembolso completo si cancelas con mas de 7 dias', 'Eg: Full refund if canceled more than 7 days before check-in')}
                  />
                  {formData.cancellation_policy.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeArrayField('cancellation_policy', index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addArrayField('cancellation_policy')}
                className="mt-2"
              >
                {tr('Anadir condicion', 'Add condition')}
              </Button>
            </div>

            {showTranslationFields && (
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-4 space-y-4">
                <p className="text-sm font-semibold text-blue-900">{tr('Normas y politica en ingles', 'Rules and policy in English')}</p>
                <div>
                  <Label>House rules in English</Label>
                  {formData.rules_en.map((rule, index) => (
                    <div key={index} className="flex gap-2 mt-2">
                      <Input
                        value={rule}
                        onChange={(e) => handleArrayChange('rules_en', index, e.target.value)}
                        placeholder="Eg: No smoking indoors"
                      />
                      {formData.rules_en.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeArrayField('rules_en', index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addArrayField('rules_en')}
                    className="mt-2"
                  >
                    {tr('Anadir regla en ingles', 'Add rule in English')}
                  </Button>
                </div>

                <div>
                  <Label>Cancellation policy in English</Label>
                  {formData.cancellation_policy_en.map((condition, index) => (
                    <div key={index} className="flex gap-2 mt-2">
                      <Input
                        value={condition}
                        onChange={(e) => handleArrayChange('cancellation_policy_en', index, e.target.value)}
                        placeholder="Eg: Full refund if canceled more than 7 days before check-in"
                      />
                      {formData.cancellation_policy_en.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeArrayField('cancellation_policy_en', index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addArrayField('cancellation_policy_en')}
                    className="mt-2"
                  >
                    Add condition in English
                  </Button>
                </div>
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">{tr('Servicios del Alojamiento', 'Property Services')}</h3>
            <p className="text-sm text-gray-600">{tr('Selecciona los servicios disponibles', 'Select available services')}</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {SERVICES_LIST.map((service) => {
                const ServiceIcon = SERVICE_ICONS[service];
                return (
                  <div key={service} className="flex items-center space-x-2">
                    <Checkbox
                      id={service}
                      checked={formData.services.includes(service)}
                      onCheckedChange={() => handleServiceToggle(service)}
                    />
                    <label
                      htmlFor={service}
                      className="flex items-center gap-1.5 text-sm font-medium cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {ServiceIcon && <ServiceIcon className="h-4 w-4 text-teal-600 flex-shrink-0" />}
                      {service}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 6: {
        const isAdvancedPricing = formData.pricing_mode === 'advanced';

        const switchToAdvanced = () => {
          const base = parseFloat(formData.min_price) || 0;
          handleChange('pricing_mode', 'advanced');
          handleChange('base_price', base.toString());
        };

        const switchToSimple = () => {
          handleChange('pricing_mode', 'simple');
        };

        const addSeasonalRate = () => {
          const newRate = {
            id: `season_${Date.now()}`,
            label: '',
            date_start: '',
            date_end: '',
            rate_type: 'percentage',
            rate_value: '',
          };
          handleChange('seasonal_rates', [...(formData.seasonal_rates || []), newRate]);
        };

        const updateSeasonalRate = (index, field, value) => {
          const updated = (formData.seasonal_rates || []).map((r, i) =>
            i === index ? { ...r, [field]: value } : r
          );
          handleChange('seasonal_rates', updated);
        };

        const removeSeasonalRate = (index) => {
          handleChange('seasonal_rates', (formData.seasonal_rates || []).filter((_, i) => i !== index));
        };

        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{tr('Precios y Enlaces', 'Pricing and Links')}</h3>              <div className="flex items-center gap-3 rounded-lg border px-3 py-2 bg-gray-50">
                <span className={`text-sm font-medium ${!isAdvancedPricing ? 'text-gray-900' : 'text-gray-400'}`}>{tr('Simple', 'Simple')}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isAdvancedPricing}
                  onClick={isAdvancedPricing ? switchToSimple : switchToAdvanced}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${isAdvancedPricing ? 'bg-teal-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isAdvancedPricing ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className={`text-sm font-medium ${isAdvancedPricing ? 'text-teal-700' : 'text-gray-400'}`}>{tr('Avanzado', 'Advanced')}</span>
              </div>
            </div>

            {!isAdvancedPricing ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="min_price">{tr('Precio Minimo por Noche (€)', 'Minimum Price per Night (€)')}</Label>
                  <Input
                    id="min_price"
                    type="number"
                    min="0"
                    value={formData.min_price}
                    onChange={(e) => handleChange('min_price', e.target.value)}
                    placeholder="50"
                  />
                </div>
                <div>
                  <Label htmlFor="max_price">{tr('Precio Maximo por Noche (€)', 'Maximum Price per Night (€)')}</Label>
                  <Input
                    id="max_price"
                    type="number"
                    min="0"
                    value={formData.max_price}
                    onChange={(e) => handleChange('max_price', e.target.value)}
                    placeholder="150"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-6 rounded-lg border border-teal-100 bg-teal-50/40 p-4">
                <p className="text-xs text-teal-700">{tr('En modo avanzado, el Precio base es el punto de partida. Las temporadas y suplementos se aplican sobre el.', 'In advanced mode, the Base price is the starting point. Seasonal rates and surcharges are applied on top of it.')}</p>

                {/* Precio base */}
                <div className="max-w-xs">
                  <Label htmlFor="base_price">{tr('Precio base por noche (€) *', 'Base price per night (€) *')}</Label>
                  <Input
                    id="base_price"
                    type="number"
                    min="0"
                    value={formData.base_price}
                    onChange={(e) => handleChange('base_price', e.target.value)}
                    placeholder="80"
                  />
                  <p className="text-xs text-gray-500 mt-1">{tr('Precio de referencia; minimo y maximo informativos se calculan del rango de temporadas.', 'Reference price; informative min and max are calculated from seasonal ranges.')}</p>
                </div>

                {/* Descuentos por duración */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">{tr('Descuentos por duracion', 'Duration discounts')}</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="discount_week">{tr('Descuento semana (7+ noches) %', 'Weekly discount (7+ nights) %')}</Label>
                      <Input
                        id="discount_week"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.duration_discounts?.week ?? ''}
                        onChange={(e) => handleChange('duration_discounts', { ...formData.duration_discounts, week: e.target.value })}
                        placeholder="10"
                      />
                    </div>
                    <div>
                      <Label htmlFor="discount_month">{tr('Descuento mes (30+ noches) %', 'Monthly discount (30+ nights) %')}</Label>
                      <Input
                        id="discount_month"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.duration_discounts?.month ?? ''}
                        onChange={(e) => handleChange('duration_discounts', { ...formData.duration_discounts, month: e.target.value })}
                        placeholder="20"
                      />
                    </div>
                  </div>
                </div>

                {/* Suplemento fin de semana */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="weekend_enabled"
                      checked={Boolean(formData.weekend_surcharge?.enabled)}
                      onChange={(e) => handleChange('weekend_surcharge', { ...formData.weekend_surcharge, enabled: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-teal-600"
                    />
                    <Label htmlFor="weekend_enabled" className="mb-0 cursor-pointer">{tr('Suplemento fin de semana (viernes-sabado)', 'Weekend surcharge (Friday-Saturday)')}</Label>
                  </div>
                  {formData.weekend_surcharge?.enabled && (
                    <div className="flex gap-3 items-end mt-2">
                      <div className="flex-1">
                        <Label>{tr('Tipo', 'Type')}</Label>
                        <select
                          value={formData.weekend_surcharge?.type || 'percentage'}
                          onChange={(e) => handleChange('weekend_surcharge', { ...formData.weekend_surcharge, type: e.target.value })}
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                        >
                          <option value="percentage">{tr('Porcentaje (%)', 'Percentage (%)')}</option>
                          <option value="fixed">{tr('Importe fijo (€)', 'Fixed amount (€)')}</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <Label>{tr('Valor', 'Value')}</Label>
                        <Input
                          type="number"
                          min="0"
                          value={formData.weekend_surcharge?.value ?? ''}
                          onChange={(e) => handleChange('weekend_surcharge', { ...formData.weekend_surcharge, value: e.target.value })}
                          placeholder={formData.weekend_surcharge?.type === 'fixed' ? '20' : '15'}
                        />
                      </div>
                      <p className="text-sm text-gray-500 mb-2">{formData.weekend_surcharge?.type === 'fixed' ? tr('€ extra/noche', '€ extra/night') : tr('% sobre base', '% over base')}</p>
                    </div>
                  )}
                </div>

                {/* Temporadas */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-gray-700">{tr('Temporadas', 'Seasons')}</p>
                    <Button type="button" variant="outline" size="sm" onClick={addSeasonalRate}>+ {tr('Anadir temporada', 'Add season')}</Button>
                  </div>
                  {(formData.seasonal_rates || []).length === 0 && (
                    <p className="text-xs text-gray-400">{tr('No hay temporadas definidas. Pulsa "Anadir temporada" para crear rangos de fechas con precios distintos.', 'No seasons defined yet. Click "Add season" to create date ranges with different prices.')}</p>
                  )}
                  <div className="space-y-3">
                    {(formData.seasonal_rates || []).map((rate, idx) => (
                      <div key={rate.id || idx} className="rounded-md border bg-white p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <Input
                            placeholder={tr('Nombre (ej: Verano)', 'Name (eg: Summer)')}
                            value={rate.label}
                            onChange={(e) => updateSeasonalRate(idx, 'label', e.target.value)}
                            className="flex-1"
                          />
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeSeasonalRate(idx)} className="text-red-500 hover:text-red-700">✕</Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">{tr('Desde', 'From')}</Label>
                            <Input type="date" value={rate.date_start} onChange={(e) => updateSeasonalRate(idx, 'date_start', e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">{tr('Hasta', 'To')}</Label>
                            <Input type="date" value={rate.date_end} onChange={(e) => updateSeasonalRate(idx, 'date_end', e.target.value)} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 items-end">
                          <div>
                            <Label className="text-xs">{tr('Tipo de ajuste', 'Adjustment type')}</Label>
                            <select
                              value={rate.rate_type}
                              onChange={(e) => updateSeasonalRate(idx, 'rate_type', e.target.value)}
                              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                            >
                              <option value="percentage">{tr('Porcentaje sobre base (%)', 'Percentage over base (%)')}</option>
                              <option value="fixed">{tr('Precio fijo (€/noche)', 'Fixed price (€/night)')}</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">{rate.rate_type === 'fixed' ? tr('Precio (€)', 'Price (€)') : tr('Variacion (%)', 'Change (%)')}</Label>
                            <Input
                              type="number"
                              value={rate.rate_value}
                              onChange={(e) => updateSeasonalRate(idx, 'rate_value', e.target.value)}
                              placeholder={rate.rate_type === 'fixed' ? '120' : '+25 o -15'}
                            />
                          </div>
                        </div>
                        {rate.rate_type === 'percentage' && rate.rate_value !== '' && !isNaN(parseFloat(rate.rate_value)) && parseFloat(formData.base_price) > 0 && (
                          <p className="text-xs text-teal-700">
                            {tr('Preview', 'Preview')}: {Math.round(parseFloat(formData.base_price) * (1 + parseFloat(rate.rate_value) / 100))}{tr('€/noche', '€/night')}
                          </p>
                        )}
                        {rate.rate_type === 'fixed' && rate.rate_value !== '' && (
                          <p className="text-xs text-teal-700">
                            {tr('Preview', 'Preview')}: {parseFloat(rate.rate_value) || 0}{tr('€/noche (precio fijo)', '€/night (fixed price)')}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>


              </div>
            )}

            <div>
              <Label>{tr('Perfiles en plataformas de alojamiento', 'Profiles on booking platforms')}</Label>
              <p className="text-xs text-gray-500 mb-2">{tr('Anade aqui los enlaces a tu alojamiento en Airbnb, Booking u otras plataformas. Esto da confianza a los huespedes, que podran ver opiniones, disponibilidad y mas detalles antes de contactarte.', 'Add links to your listing on Airbnb, Booking or other platforms. This builds trust, as guests can check reviews, availability and more details before contacting you.')}</p>
              {formData.booking_links.map((url, index) => (
                <div key={index} className="flex gap-2 mt-2">
                  <Input
                    value={url}
                    onChange={(e) => handleArrayChange('booking_links', index, e.target.value)}
                    placeholder="https://airbnb.com/rooms/..."
                  />
                  {formData.booking_links.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeArrayField('booking_links', index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addArrayField('booking_links')}
                className="mt-2"
              >
                {tr('Anadir enlace', 'Add link')}
              </Button>
            </div>

            {(formData.plan === 'plus' || formData.plan === 'premium') && (
              <div className="border-t pt-4">
                <Label>{tr('Calendarios ICS de disponibilidad', 'ICS availability calendars')}</Label>
                <p className="text-xs text-gray-500 mb-2">{tr('Pega las URLs de exportacion iCal de Airbnb, Booking u otras plataformas para sincronizar fechas bloqueadas.', 'Paste iCal export URLs from Airbnb, Booking or other platforms to sync blocked dates.')}</p>
                {formData.ics_urls.map((url, index) => (
                  <div key={index} className="flex gap-2 mt-2">
                    <Input
                      value={url}
                      onChange={(e) => handleArrayChange('ics_urls', index, e.target.value)}
                      placeholder="https://airbnb.com/calendar/ical/..."
                    />
                    {formData.ics_urls.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeArrayField('ics_urls', index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addArrayField('ics_urls')}
                  className="mt-2"
                >
                  {tr('Anadir calendario', 'Add calendar')}
                </Button>
              </div>
            )}
          </div>
        );
      }

      case 7:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">{tr('Informacion de Contacto', 'Contact Information')}</h3>
            
            <div>
              <Label htmlFor="whatsapp">{tr('Telefono WhatsApp *', 'WhatsApp Phone *')}</Label>
              <Input
                id="whatsapp"
                value={formData.whatsapp}
                onChange={(e) => handleChange('whatsapp', e.target.value)}
                placeholder="+34 600 000 000"
              />
            </div>

            <div>
              <Label htmlFor="email">{tr('Email de Contacto *', 'Contact Email *')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="contacto@ejemplo.com"
              />
            </div>

            <div className="border-t pt-4 space-y-4">
              <div>
                <Label htmlFor="preferred_netlify_subdomain">{tr('URL preferida (opcional)', 'Preferred URL (optional)')}</Label>
                <div className="mt-1 flex items-center rounded-md border bg-white focus-within:ring-2 focus-within:ring-teal-500">
                  <Input
                    id="preferred_netlify_subdomain"
                    value={formData.preferred_netlify_subdomain}
                    onChange={(e) => handleChange('preferred_netlify_subdomain', e.target.value.toLowerCase())}
                    placeholder={tr('mi-alojamiento', 'my-property')}
                    className="border-0 shadow-none focus-visible:ring-0"
                  />
                  <span className="pr-3 text-sm text-gray-500 whitespace-nowrap">.netlify.app</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {tr('Indica la URL que te gustaria para tu web. Esta solicitud esta sujeta a disponibilidad.', 'Enter your preferred URL for your site. This request is subject to availability.')}
                </p>
              </div>

              {formData.plan === 'premium' && (
                <div>
                <Label htmlFor="custom_domain">{tr('Dominio Personalizado (Plan Premium)', 'Custom Domain (Premium Plan)')}</Label>
                <Input
                  id="custom_domain"
                  value={formData.custom_domain}
                  onChange={(e) => handleChange('custom_domain', e.target.value)}
                  placeholder="www.mialojamiento.com"
                />
                <p className="text-sm text-gray-500 mt-1">
                  {tr('Deberas configurar los DNS de tu dominio despues de la publicacion. La publicacion con dominio propio puede tardar mas de 48h.', 'You will need to configure your domain DNS after publishing. Publishing with a custom domain may take more than 48h.')}
                </p>
              </div>
              )}
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">{tr('Galeria de Fotos', 'Photo Gallery')}</h3>
            <p className="text-sm text-gray-600">{tr('Sube hasta', 'Upload up to')} {maxImages} {tr('imagenes de tu alojamiento', 'property images')}</p>

            <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-opacity ${uploadingImages ? 'opacity-50 pointer-events-none' : ''}`}>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
                disabled={uploadingImages}
              />
              <label htmlFor="image-upload" className={uploadingImages ? 'cursor-not-allowed' : 'cursor-pointer'}>
                {uploadingImages ? (
                  <>
                    <div className="h-12 w-12 mx-auto mb-2 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-teal-600 font-medium">{tr('Subiendo imagenes...', 'Uploading images...')}</p>
                  </>
                ) : (
                  <>
                    <Upload className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">{tr('Click para subir imagenes o arrastra aqui', 'Click to upload images or drag files here')}</p>
                  </>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {formData.images.length}/{maxImages} {tr('imagenes subidas', 'images uploaded')}
                </p>
              </label>
            </div>

            {imageUploadProgress.length > 0 && (
              <div className="space-y-2 rounded-lg border p-3 bg-gray-50">
                <p className="text-xs font-medium text-gray-700">{tr('Progreso de subida', 'Upload progress')}</p>
                {imageUploadProgress.map((item) => (
                  <div key={item.id} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="truncate max-w-[70%]">{item.name}</span>
                      <span className="text-gray-600">
                        {item.status === 'compressing' && tr('Comprimiendo...', 'Compressing...')}
                        {item.status === 'uploading' && `${item.progress}%`}
                        {item.status === 'done' && `OK · ${formatBytes(item.finalSize)}`}
                        {item.status === 'error' && tr('Error', 'Error')}
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded bg-gray-200 overflow-hidden">
                      <div
                        className={`h-full transition-all ${item.status === 'error' ? 'bg-red-500' : 'bg-teal-500'}`}
                        style={{ width: `${item.status === 'compressing' ? 10 : item.progress || 0}%` }}
                      />
                    </div>
                    {(item.originalSize || item.finalSize || item.message) && (
                      <p className="text-[11px] text-gray-500">
                        {item.originalSize ? `Original: ${formatBytes(item.originalSize)}` : ''}
                        {item.finalSize ? ` → Subida: ${formatBytes(item.finalSize)}` : ''}
                        {item.message ? ` · ${item.message}` : ''}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {formData.images.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <GripVertical className="h-3 w-3" /> {tr('Arrastra las imagenes para cambiar el orden', 'Drag images to reorder')}
                  </p>
                  {(isEditMode || isClientEditMode) && (
                    <button
                      type="button"
                      onClick={handleClearAllImages}
                      disabled={uploadingImages}
                      className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-3 w-3" />
                      {tr('Borrar todas las fotos', 'Delete all photos')}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {formData.images.map((image, index) => (
                    <div
                      key={image.url}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className="relative group cursor-grab active:cursor-grabbing"
                    >
                      <img
                        src={image.url}
                        alt={`Imagen ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg pointer-events-none"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        disabled={uploadingImages}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <span className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                        {index === 0 ? tr('Portada', 'Cover') : `${tr('Imagen', 'Image')} ${index + 1}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const steps = [
    { number: 1, title: t.steps[0] },
    { number: 2, title: t.steps[1] },
    { number: 3, title: t.steps[2] },
    { number: 4, title: t.steps[3] },
    { number: 5, title: t.steps[4] },
    { number: 6, title: t.steps[5] },
    { number: 7, title: t.steps[6] },
    { number: 8, title: t.steps[7] }
  ];

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t.loadingPropertyData}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">
            {isClientEditMode
              ? t.titleClientEdit
              : isEditMode
                ? t.titleEdit
                : isCloneMode
                  ? t.titleClone
                  : t.titleCreate}
          </h1>
          <p className="text-gray-600">
            {isClientEditMode
              ? t.subtitleClientEdit
              : isEditMode
                ? t.subtitleEdit
                : isCloneMode
                  ? t.subtitleClone
                  : t.subtitleCreate}
          </p>
          <p className="mt-3 flex items-center justify-center gap-2 text-sm text-gray-500">
            <Mail className="h-4 w-4" />
            <span>{t.contactLabel}</span>
            <a href="mailto:info@miwebvacacional.online" className="hover:text-teal-700 transition-colors">
              info@miwebvacacional.online
            </a>
          </p>
          {isCloneMode && (
            <p className="mx-auto mt-4 max-w-2xl rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {t.cloneWarningPrefix} {cloningLicense}. {t.cloneWarningText}
            </p>
          )}
          {isEditMode && (
            <div className="flex flex-col sm:flex-row justify-center gap-3 mt-4">
              <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
                {t.backToDashboard}
              </Button>
              <Button variant="outline" onClick={() => navigate(`/preview/${editingLicense}`)}>
                {t.viewCurrentPreview}
              </Button>
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="mb-8 overflow-x-auto pb-2">
          <div className="flex justify-between items-center min-w-[720px]">
            {steps.map((step) => (
              <div key={step.number} className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    currentStep >= step.number
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step.number}
                </div>
                <span className="text-xs mt-1 hidden md:block">{step.title}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 h-2 bg-gray-200 rounded-full">
            <div
              className="h-full bg-teal-600 rounded-full transition-all"
              style={{ width: `${(currentStep / 8) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t.stepOf} {currentStep} {t.of} 8</CardTitle>
            <CardDescription>{steps[currentStep - 1].title}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderStep()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                {t.previous}
              </Button>

              {currentStep < 8 ? (
                <Button type="button" onClick={nextStep}>
                  {t.next}
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <>
                  {isClientEditMode ? (
                    <Button
                      type="button"
                      onClick={() => {
                        if (!validateStep(8)) return;
                        // Save the exact payload we will send to backend, so preview and payment are consistent
                        const propertyPayload = buildPropertyPayload();
                        sessionStorage.setItem('modification_preview_data', JSON.stringify({ property_payload: propertyPayload }));
                        navigate(`/manage/${manageToken}/preview`);
                      }}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      {t.previewChanges}
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {loading ? t.saving : isEditMode ? t.saveChanges : isCloneMode ? t.createCopy : t.viewPreview}
                    </Button>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
